import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { 
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "../../store";
import { selectErrorDetailsModal, closeErrorDetailsModal } from "../../store/adminSlice";
import { TimestampDisplay } from "../../internal/components/TimestampDisplay";
import JSONString from "./JSONString";

interface ErrorSection {
  title: string;
  icon: React.ReactElement;
  severity: "error" | "warning" | "info";
  items: any[];
}

export function ErrorDetailsModal() {
  const dispatch = useDispatch<AppDispatch>();
  const { open, log } = useSelector(selectErrorDetailsModal);

  const handleClose = () => {
    dispatch(closeErrorDetailsModal());
  };

  if (!log) return null;

  // Extract different types of errors/warnings/info from the log
  const errorSections: ErrorSection[] = [];

  if (log.extra?.errors) {
    const errors = Array.isArray(log.extra.errors) ? log.extra.errors : [log.extra.errors];
    if (errors.length > 0) {
      errorSections.push({
        title: "Errors",
        icon: <ErrorIcon />,
        severity: "error",
        items: errors,
      });
    }
  }

  if (log.extra?.error) {
    const errors = Array.isArray(log.extra.error) ? log.extra.error : [log.extra.error];
    errorSections.push({
      title: "Error",
      icon: <ErrorIcon />,
      severity: "error",
      items: errors,
    });
  }

  if (log.extra?.warnings) {
    const warnings = Array.isArray(log.extra.warnings) ? log.extra.warnings : [log.extra.warnings];
    if (warnings.length > 0) {
      errorSections.push({
        title: "Warnings",
        icon: <WarningIcon />,
        severity: "warning",
        items: warnings,
      });
    }
  }

  if (log.extra?.warning) {
    const warnings = Array.isArray(log.extra.warning) ? log.extra.warning : [log.extra.warning];
    errorSections.push({
      title: "Warning",
      icon: <WarningIcon />,
      severity: "warning",
      items: warnings,
    });
  }

  if (log.extra?.infos) {
    const infos = Array.isArray(log.extra.infos) ? log.extra.infos : [log.extra.infos];
    if (infos.length > 0) {
      errorSections.push({
        title: "Info",
        icon: <InfoIcon />,
        severity: "info",
        items: infos,
      });
    }
  }

  if (log.extra?.info) {
    const infos = Array.isArray(log.extra.info) ? log.extra.info : [log.extra.info];
    errorSections.push({
      title: "Info",
      icon: <InfoIcon />,
      severity: "info",
      items: infos,
    });
  }

  const hasNoErrors = errorSections.length === 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: "80vh" }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon color="error" />
            <Typography variant="h6">
              Error Details
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Log Basic Info */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Action Log Info
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Chip label={log.type} size="small" />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Created At
                </Typography>
                <TimestampDisplay timestamp={log.createdAt} />
              </Box>
            </Box>
            {log.memo && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Memo
                </Typography>
                <Typography variant="body2">
                  {log.memo}
                </Typography>
              </Box>
            )}
          </Box>

          {/* No Errors Message */}
          {hasNoErrors && (
            <Alert severity="info">
              No errors, warnings, or info messages found in this action log.
            </Alert>
          )}

          {/* Error Sections */}
          {errorSections.map((section, index) => (
            <Accordion key={`${section.title}-${index}`} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {section.icon}
                  <Typography variant="subtitle1">
                    {section.title} ({section.items.length})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {section.items.map((item, itemIndex) => (
                    <Alert 
                      key={itemIndex} 
                      severity={section.severity}
                      sx={{ 
                        "& .MuiAlert-message": { 
                          width: "100%",
                          overflow: "auto"
                        }
                      }}
                    >
                      {typeof item === "string" ? (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: "0.875rem"
                          }}
                        >
                          {item}
                        </Typography>
                      ) : (
                        <JSONString data={item} />
                      )}
                    </Alert>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Full Extra Data for Reference */}
          {log.extra && !hasNoErrors && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  Full Extra Data (for reference)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <JSONString data={log.extra} />
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}